import {Component, computed, inject, signal} from '@angular/core';
import {BaseModalComponent} from "../../../../../../core/components/modals/base-modal/base-modal.component";
import {MatButton} from "@angular/material/button";
import {unitEconomicsCalculationsResourceType} from "./configs/unit-economics-calculations-config";
import {MAT_DIALOG_DATA} from "@angular/material/dialog";
import {
  UnitEconomicsCalculationData,
  UnitEconomicsCalculationDialogData
} from "./types/unit-economics-calculations.types";
import {UnitEconomicsCalculationsService} from "./services/unit-economics-calculations.service";
import {FormGroup} from "@angular/forms";
import {UnitEconomicsCalculationsFormField} from "./types/unit-economics-calculations-form-field.types";
import {unitEconomicsCalculationsFormFields} from "./configs/unit-economics-calculations-form-fields";
import {defaultUnitEconomicsCalculationData} from "./configs/default-unit-economics-calculation-data";
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from "@angular/material/expansion";
import {
  UnitEconomicsCalculationsTitleComponent
} from "./components/unit-economics-calculations-title/unit-economics-calculations-title.component";
import {ProductCardFormFieldService} from "../../../services/product-card-form-field.service";
import {
  UnitEconomicsCalculationsFormComponent
} from "./components/unit-economics-calculations-form/unit-economics-calculations-form.component";
import {EditModeService} from "../../../../../../core/services/edit-mode.service";
import {
  UnitEconomicsCalculationsFilesComponent
} from "./components/unit-economics-calculations-files/unit-economics-calculations-files.component";
import {LoaderComponent} from "../../../../../../shared/components/loader/loader.component";

@Component({
  selector: 'app-unit-economics-calculations-modal',
  imports: [
    BaseModalComponent,
    MatButton,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
    UnitEconomicsCalculationsTitleComponent,
    UnitEconomicsCalculationsFormComponent,
    UnitEconomicsCalculationsFilesComponent,
    LoaderComponent
  ],
  templateUrl: './unit-economics-calculations-modal.component.html',
  styleUrl: './unit-economics-calculations-modal.component.scss',
  providers: [ EditModeService ]
})
export class UnitEconomicsCalculationsModalComponent extends BaseModalComponent {
  private unitEconomicsCalculationDialogData = inject<UnitEconomicsCalculationDialogData>(MAT_DIALOG_DATA);
  private unitEconomicsCalculationsService: UnitEconomicsCalculationsService = inject(UnitEconomicsCalculationsService);
  private productCardFormFieldService: ProductCardFormFieldService = inject(ProductCardFormFieldService);

  public readonly unitEconomicsCalculationsResourceType = unitEconomicsCalculationsResourceType;
  public unitEconomicsCalculationsData = signal<Array<UnitEconomicsCalculationData>>([]);
  public unitEconomicsCalculationFormGroups = signal<Array<FormGroup>>([]);
  public isValid = signal<boolean>(false);
  public noFirstData = signal<boolean>(false);
  public technicalParametersId = computed(() => this.unitEconomicsCalculationDialogData.parentId);

  public override async ngOnInit(): Promise<void> {
    super.ngOnInit();
    await this.initModal();
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.modalStatesService.closeModal(unitEconomicsCalculationsResourceType.key);
  }

  public handleAddNew(): void {
    const formGroup = this.baseModalService.getFormGroup<UnitEconomicsCalculationsFormField>(unitEconomicsCalculationsFormFields);
    this.unitEconomicsCalculationFormGroups.update(forms  => [ ...forms, formGroup ]);
    this.unitEconomicsCalculationsData.update(data  => [ ...data, defaultUnitEconomicsCalculationData ]);
    this.isValid.set(false);
  }

  public async handleDelete(event: MouseEvent, id: number, index: number): Promise<void> {
    event.stopPropagation();
    const isConfirmed = await this.productCardFormFieldService.isDeleteConfirmed(id);
    if (!isConfirmed) { return; }

    this.loadingService.setIsLoadingState(true);
    await this.deleteRecord(unitEconomicsCalculationsResourceType.path, id)
      .then(() => this.removeUnitEconomicAtIndex(index))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  public updateUnitEconomicData(data: UnitEconomicsCalculationData, index: number): void {
    this.unitEconomicsCalculationsData()[index] = data;
    this.unitEconomicsCalculationFormGroups().at(index)?.patchValue(data);
    this.isValid.set(!!data.id);
    this.noFirstData.set(!!data.id);
  }

  public deleteUnitEconomicData(index: number): void {
    this.unitEconomicsCalculationsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    this.unitEconomicsCalculationFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.isValid.set(true);
  }

  public setLoadingState(isLoading: boolean): void {
    this.loadingService.setIsLoadingState(isLoading);
  }

  private async initModal(): Promise<void> {
    this.loadingService.setIsLoadingState(true);
    this.modalStatesService.openModal(unitEconomicsCalculationsResourceType.key);
    await this.unitEconomicsCalculationsService.getDataInModal<UnitEconomicsCalculationData>(this.technicalParametersId())
      .then((data) => this.successLoad(data))
      .finally(() => this.loadingService.setIsLoadingState(false));
  }

  private successLoad(data: Array<UnitEconomicsCalculationData>): void {
    this.unitEconomicsCalculationsData.set(data);
    this.isValid.set(!!this.unitEconomicsCalculationsData()?.at(0)?.id);
    this.noFirstData.set(!!this.unitEconomicsCalculationsData()?.at(0)?.id);
    this.initForm();
  }

  private initForm(): void {
    if (!this.unitEconomicsCalculationsData().length) {
      this.handleAddNew();
      return;
    }

    const formGroups = this.unitEconomicsCalculationsData().map(data => {
      const formGroup = this.baseModalService.getFormGroup<UnitEconomicsCalculationsFormField>(unitEconomicsCalculationsFormFields);
      formGroup.patchValue(data);
      return formGroup;
    });

    this.unitEconomicsCalculationFormGroups.set(formGroups);
  }

  private removeUnitEconomicAtIndex(index: number): void {
    this.unitEconomicsCalculationFormGroups.update(groups => {
      const newGroups = [...groups];
      newGroups.splice(index, 1);
      return newGroups;
    });

    this.unitEconomicsCalculationsData.update(data => {
      const newData = [...data];
      newData.splice(index, 1);
      return newData;
    });

    if (!this.unitEconomicsCalculationsData().length) {
      this.noFirstData.set(false);
      this.handleAddNew();
    }

    this.isValid.set(!!this.unitEconomicsCalculationsData().length);
  }
}
